/**
 * Thin fetch-based HTTP client used by every server query/mutation.
 *
 * Responsibilities:
 * - Inject the bearer access token on every request.
 * - On a 401, attempt ONE refresh against `/v1/auth/refresh` and retry the
 *   original request once. If the refresh fails, clear tokens and throw an
 *   `UnauthorizedError` so route guards can react.
 * - Map non-2xx JSON responses (`{error, message, details?}`) to a typed
 *   `ApiError` so callers can branch on `err.code`.
 *
 * Idempotency keys are opt-in — pass `{headers: {'Idempotency-Key': ...}}`
 * when calling a mutating endpoint that requires it. We never auto-generate
 * one to avoid silently bypassing real idempotency requirements.
 */

import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './auth';

const DEFAULT_BASE_URL = 'http://localhost:8080';

export function getBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  return typeof fromEnv === 'string' && fromEnv.length > 0 ? fromEnv : DEFAULT_BASE_URL;
}

export interface ApiErrorPayload {
  error?: string;
  message?: string;
  details?: unknown;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Thrown after a failed refresh attempt — callers (route guards, useAuth)
 * should treat this as "user is signed out, redirect to /login".
 */
export class UnauthorizedError extends ApiError {
  constructor(message = 'Unauthorized') {
    super(401, 'unauthorized', message);
    this.name = 'UnauthorizedError';
  }
}

interface AuthRefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestParams {
  method: HttpMethod;
  path: string;
  body?: unknown;
  options?: RequestInit;
}

function mergeHeaders(...sources: Array<HeadersInit | undefined>): Headers {
  const headers = new Headers();
  for (const source of sources) {
    if (!source) continue;
    new Headers(source).forEach((value, key) => {
      headers.set(key, value);
    });
  }
  return headers;
}

async function parseError(response: Response): Promise<ApiError> {
  let payload: ApiErrorPayload | null = null;
  try {
    const text = await response.text();
    payload = text ? (JSON.parse(text) as ApiErrorPayload) : null;
  } catch {
    payload = null;
  }
  const code = payload?.error ?? `http_${response.status}`;
  const message = payload?.message ?? response.statusText ?? 'Request failed';
  return new ApiError(response.status, code, message, payload?.details);
}

async function parseJsonBody<T>(response: Response): Promise<T> {
  // 204 No Content / empty bodies still satisfy `Promise<T>` for callers that
  // type the result as `void`. Returning `undefined as T` keeps the contract.
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

let inFlightRefresh: Promise<string | null> | null = null;

/**
 * Single-flight refresh: if multiple requests 401 simultaneously, only one
 * `/v1/auth/refresh` call is sent and the rest await its result.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  inFlightRefresh = (async () => {
    try {
      const response = await fetch(`${getBaseUrl()}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!response.ok) {
        clearTokens();
        return null;
      }
      const data = (await response.json()) as AuthRefreshResponse;
      setTokens(data.access_token, data.refresh_token);
      return data.access_token;
    } catch {
      clearTokens();
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

async function executeRequest({ method, path, body, options }: RequestParams): Promise<Response> {
  const accessToken = getAccessToken();
  const headers = mergeHeaders(
    body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
    options?.headers,
  );
  return fetch(`${getBaseUrl()}${path}`, {
    ...options,
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : options?.body,
  });
}

async function request<T>(params: RequestParams): Promise<T> {
  let response = await executeRequest(params);

  if (response.status === 401 && params.path !== '/v1/auth/refresh' && params.path !== '/v1/auth/login') {
    const newAccessToken = await refreshAccessToken();
    if (!newAccessToken) {
      throw new UnauthorizedError();
    }
    response = await executeRequest(params);
    if (response.status === 401) {
      clearTokens();
      throw new UnauthorizedError();
    }
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  return parseJsonBody<T>(response);
}

export const api = {
  get<T>(path: string, options?: RequestInit): Promise<T> {
    return request<T>({ method: 'GET', path, options });
  },
  post<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return request<T>({ method: 'POST', path, body, options });
  },
  put<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return request<T>({ method: 'PUT', path, body, options });
  },
  patch<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return request<T>({ method: 'PATCH', path, body, options });
  },
  delete<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return request<T>({ method: 'DELETE', path, body, options });
  },
};

export type { HttpMethod };
